import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPublishedPosts } from "../../services/api";
import SEOHead from "../../components/SEO/SEOHead";
import Header from "../../components/Header/Header";
import Rodape from "../../components/Rodape/Rodape";
import { resolveImageUrl } from "../../utils/driveImage";
import styles from "./BlogListing.module.css";

const CATEGORY_PALETTE = [
    { bg: "#f0f9ff", color: "#0284c7" }, // azul
    { bg: "#f0fdf4", color: "#16a34a" }, // verde
    { bg: "#f5f3ff", color: "#7c3aed" }, // roxo
    { bg: "#fffbeb", color: "#d97706" }, // âmbar
    { bg: "#fdf2f8", color: "#db2777" }, // rosa
    { bg: "#ecfeff", color: "#0891b2" }, // ciano
];

function categoryColor(name = "") {
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = (hash * 31 + name.charCodeAt(i)) % 997;
    }
    return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

function formatDate(value) {
    if (!value) return "";

    const normalized = value.endsWith("Z")
        ? value
        : `${value.replace("+00:00", "")}Z`;

    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "long",
    }).format(date);
}

function normalizeText(value = "") {
    return value
        .toString()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
}

function SearchIcon() {
    return (
        <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}

function CategoryBadge({ category }) {
    if (!category) return null;

    const { bg, color } = categoryColor(category);

    return (
        <span
            className={styles.category}
            style={{ background: bg, color }}
        >
            {category}
        </span>
    );
}

function CoverFallback() {
    return (
        <div className={styles.coverFallback}>
            <span>HL</span>
        </div>
    );
}

function CardFooter({ post }) {
    if (!post.author && !post.reading_time) return null;

    return (
        <div className={styles.cardFooter}>
            {post.author && (
                <span className={styles.cardAuthor}>{post.author}</span>
            )}
            {post.reading_time && (
                <span className={styles.cardReadTime}>
                    {post.reading_time} min de leitura
                </span>
            )}
        </div>
    );
}

function FeaturedCard({ post }) {
    const coverUrl = resolveImageUrl(post.cover_image);

    return (
        <Link to={`/blog/${post.slug}`} className={styles.featuredCard}>
            <div className={styles.featuredCover}>
                {coverUrl ? (
                    <img src={coverUrl} alt={post.title} loading="lazy" />
                ) : (
                    <CoverFallback />
                )}
            </div>

            <div className={styles.featuredBody}>
                <span className={styles.featuredBadge}>✦ Mais recente</span>

                <div className={styles.cardMeta}>
                    <CategoryBadge category={post.category} />

                    {post.published_at && (
                        <time
                            className={styles.cardDate}
                            dateTime={post.published_at}
                        >
                            {formatDate(post.published_at)}
                        </time>
                    )}
                </div>

                <h2 className={styles.featuredTitle}>{post.title}</h2>

                {post.excerpt && (
                    <p className={styles.featuredExcerpt}>{post.excerpt}</p>
                )}

                <CardFooter post={post} />

                <span className={styles.readMore}>
                    Ler artigo <span className={styles.readMoreArrow}>→</span>
                </span>
            </div>
        </Link>
    );
}

function BlogCard({ post }) {
    const coverUrl = resolveImageUrl(post.cover_image);

    return (
        <Link to={`/blog/${post.slug}`} className={styles.card}>
            <div className={styles.cardCover}>
                {coverUrl ? (
                    <img
                        src={coverUrl}
                        alt={post.title}
                        loading="lazy"
                    />
                ) : (
                    <CoverFallback />
                )}
            </div>

            <div className={styles.cardBody}>
                <div className={styles.cardMeta}>
                    <CategoryBadge category={post.category} />

                    {post.published_at && (
                        <time
                            className={styles.cardDate}
                            dateTime={post.published_at}
                        >
                            {formatDate(post.published_at)}
                        </time>
                    )}
                </div>

                <h2 className={styles.cardTitle}>{post.title}</h2>

                {post.excerpt && (
                    <p className={styles.cardExcerpt}>{post.excerpt}</p>
                )}

                <CardFooter post={post} />

                <span className={styles.readMore}>
                    Ler artigo <span className={styles.readMoreArrow}>→</span>
                </span>
            </div>
        </Link>
    );
}

function BlogSkeleton() {
    return (
        <>
            <div className={styles.skeletonFeatured}>
                <div className={styles.skeletonCover} />
                <div className={styles.skeletonBody}>
                    <div className={styles.skeletonSmall} />
                    <div className={styles.skeletonTitle} />
                    <div className={styles.skeletonText} />
                    <div className={styles.skeletonTextShort} />
                </div>
            </div>

            <div className={styles.grid}>
                {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className={styles.skeletonCard}>
                        <div className={styles.skeletonCover} />
                        <div className={styles.skeletonBody}>
                            <div className={styles.skeletonSmall} />
                            <div className={styles.skeletonTitle} />
                            <div className={styles.skeletonText} />
                            <div className={styles.skeletonTextShort} />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

export default function BlogListing() {
    const [posts, setPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("todas");

    useEffect(() => {
        let isMounted = true;

        async function loadPosts() {
            try {
                setIsLoading(true);
                setError("");

                const data = await fetchPublishedPosts();

                if (!isMounted) return;

                setPosts(Array.isArray(data?.posts) ? data.posts : []);
            } catch (err) {
                if (!isMounted) return;

                setError(
                    err?.message ||
                    "Não foi possível carregar os posts no momento."
                );
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadPosts();

        return () => {
            isMounted = false;
        };
    }, []);

    const categories = useMemo(() => {
        const uniqueCategories = posts
            .map((post) => post.category)
            .filter(Boolean);

        return ["todas", ...new Set(uniqueCategories)];
    }, [posts]);

    const filteredPosts = useMemo(() => {
        const normalizedSearch = normalizeText(search);

        return posts.filter((post) => {
            const matchesCategory =
                selectedCategory === "todas" ||
                post.category === selectedCategory;

            const searchableContent = normalizeText(
                `${post.title || ""} ${post.excerpt || ""} ${post.category || ""}`
            );

            const matchesSearch =
                !normalizedSearch ||
                searchableContent.includes(normalizedSearch);

            return matchesCategory && matchesSearch;
        });
    }, [posts, search, selectedCategory]);

    const hasFilters = search || selectedCategory !== "todas";
    const featuredPost = !hasFilters ? filteredPosts[0] : null;
    const gridPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts;

    return (
        <>
            <SEOHead
                title="Blog HL Software — Tecnologia, SaaS e Automação para Empresas"
                description="Artigos práticos sobre desenvolvimento de software sob medida, automação de processos, SaaS, gestão empresarial e inovação digital. Conteúdo para empreendedores e empresas que querem crescer com tecnologia."
                canonical="https://www.hlsoftware.com.br/blog"
                ogType="website"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Blog",
                    name: "Blog HL Software",
                    url: "https://www.hlsoftware.com.br/blog",
                    description:
                        "Artigos sobre desenvolvimento de software, automação, SaaS e gestão de negócios.",
                    inLanguage: "pt-BR",
                    publisher: {
                        "@type": "Organization",
                        name: "HL Software",
                        url: "https://www.hlsoftware.com.br/",
                    },
                }}
            />

            <Header />

            <main className={styles.page}>
                <section className={styles.hero}>
                    <div className={styles.heroOverlay} />
                    <div className={styles.heroContent}>
                        <span className={styles.eyebrow}>Blog HL Software</span>
                        <h1>
                            Conteúdos para quem quer transformar negócios com{" "}
                            <span className={styles.heroHighlight}>
                                tecnologia
                            </span>
                        </h1>
                        <p>
                            Artigos sobre desenvolvimento de software, automação,
                            SaaS, produtividade e gestão para empresas que querem
                            evoluir com estratégia.
                        </p>
                    </div>
                </section>

                <section className={styles.content}>
                    {!isLoading && !error && posts.length > 0 && (
                        <div className={styles.toolbar}>
                            <div className={styles.searchBox}>
                                <SearchIcon />
                                <input
                                    id="blog-search"
                                    type="search"
                                    aria-label="Buscar artigo"
                                    placeholder="Busque por tema, título ou categoria..."
                                    value={search}
                                    onChange={(event) =>
                                        setSearch(event.target.value)
                                    }
                                />
                            </div>

                            <div className={styles.categoryPills}>
                                {categories.map((category) => (
                                    <button
                                        key={category}
                                        type="button"
                                        className={
                                            selectedCategory === category
                                                ? `${styles.pill} ${styles.pillActive}`
                                                : styles.pill
                                        }
                                        onClick={() =>
                                            setSelectedCategory(category)
                                        }
                                    >
                                        {category === "todas"
                                            ? "Todas"
                                            : category}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {isLoading && <BlogSkeleton />}

                    {!isLoading && error && (
                        <div className={styles.feedbackBox}>
                            <h2>Não conseguimos carregar os posts</h2>
                            <p>{error}</p>
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                            >
                                Tentar novamente
                            </button>
                        </div>
                    )}

                    {!isLoading && !error && posts.length === 0 && (
                        <div className={styles.feedbackBox}>
                            <h2>Nenhum post publicado ainda</h2>
                            <p>
                                Em breve teremos novos conteúdos sobre tecnologia,
                                automação e desenvolvimento de software.
                            </p>
                        </div>
                    )}

                    {!isLoading &&
                        !error &&
                        posts.length > 0 &&
                        filteredPosts.length === 0 && (
                            <div className={styles.feedbackBox}>
                                <h2>Nenhum artigo encontrado</h2>
                                <p>
                                    Não encontramos posts para os filtros aplicados.
                                </p>

                                {hasFilters && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearch("");
                                            setSelectedCategory("todas");
                                        }}
                                    >
                                        Limpar filtros
                                    </button>
                                )}
                            </div>
                        )}

                    {!isLoading && !error && filteredPosts.length > 0 && (
                        <>
                            <div className={styles.resultInfo}>
                                <span>
                                    {filteredPosts.length}{" "}
                                    {filteredPosts.length === 1
                                        ? "artigo encontrado"
                                        : "artigos encontrados"}
                                </span>

                                {hasFilters && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearch("");
                                            setSelectedCategory("todas");
                                        }}
                                    >
                                        Limpar filtros
                                    </button>
                                )}
                            </div>

                            {featuredPost && (
                                <FeaturedCard post={featuredPost} />
                            )}

                            {gridPosts.length > 0 && (
                                <div className={styles.grid}>
                                    {gridPosts.map((post) => (
                                        <BlogCard key={post.id} post={post} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </section>
            </main>

            <Rodape />
        </>
    );
}
